const API_BASE = '/api';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

export async function uploadImages(files, token, onProgress) {
  if (!files || files.length === 0) {
    throw new Error('No files provided for upload.');
  }

  const uploads = await Promise.all(Array.from(files).map(async (file) => {
    if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
      throw new Error(`${file.name} is not a supported image type.`);
    }
    if (file.size > MAX_IMAGE_BYTES) {
      throw new Error(`${file.name} is larger than 5 MB.`);
    }
    return {
      filename: file.name,
      contentType: file.type,
      dataUrl: await readFileAsDataUrl(file),
    };
  }));

  onProgress?.(45);

  const res = await fetch(`${API_BASE}/uploads`, {
    method: 'POST',
    headers: {
      'x-auth-token': token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ files: uploads }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.msg || 'Image upload failed.');
  }

  onProgress?.(100);
  return data.assets;
}

export async function fetchUploadAssets(token, query = '') {
  const url = `${API_BASE}/uploads?search=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    headers: { 'x-auth-token': token },
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.msg || 'Failed to fetch uploaded assets.');
  }

  return data.assets;
}

export async function replaceUploadedImage(assetId, newFile, token) {
  if (!ACCEPTED_IMAGE_TYPES.has(newFile.type)) {
    throw new Error(`${newFile.name} is not a supported image type.`);
  }
  if (newFile.size > MAX_IMAGE_BYTES) {
    throw new Error(`${newFile.name} is larger than 5 MB.`);
  }

  const dataUrl = await readFileAsDataUrl(newFile);

  const res = await fetch(`${API_BASE}/uploads/${assetId}/replace`, {
    method: 'PUT',
    headers: {
      'x-auth-token': token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      file: {
        filename: newFile.name,
        contentType: newFile.type,
        dataUrl,
      },
    }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.msg || 'Failed to replace image.');
  }

  return data.asset;
}

export async function deleteUploadedImage(assetId, token) {
  const res = await fetch(`${API_BASE}/uploads/${assetId}`, {
    method: 'DELETE',
    headers: { 'x-auth-token': token },
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.msg || 'Failed to delete image.');
  }

  return data;
}
